import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './PaymentLogs.css';

interface PaymentLog {
  id: string;
  timestamp: Timestamp;
  event: string;
  registrationId?: string;
  amount?: number;
  convenienceFee?: number;
  success: boolean;
  error?: string;
  stripePaymentId?: string;
  stripeSessionId?: string;
  userId?: string;
  metadata?: any;
}

const PaymentLogs: React.FC = () => {
  const [logs, setLogs] = useState<PaymentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');

  useEffect(() => {
    loadLogs();
  }, [filter, eventFilter]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      let q = query(collection(db, 'paymentLogs'), orderBy('timestamp', 'desc'), limit(100));

      const snapshot = await getDocs(q);
      const logsData: PaymentLog[] = [];
      snapshot.forEach((doc) => {
        logsData.push({ id: doc.id, ...doc.data() } as PaymentLog);
      });

      // Apply filters
      let filteredLogs = logsData;
      if (filter === 'success') {
        filteredLogs = filteredLogs.filter((log) => log.success);
      } else if (filter === 'failed') {
        filteredLogs = filteredLogs.filter((log) => !log.success);
      }

      if (eventFilter !== 'all') {
        filteredLogs = filteredLogs.filter((log) => log.event === eventFilter);
      }

      setLogs(filteredLogs);
    } catch (err: any) {
      console.error('Error loading payment logs:', err);
      setError(err.message || 'Failed to load payment logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const getEventTypes = () => {
    const eventTypes = new Set<string>();
    logs.forEach((log) => eventTypes.add(log.event));
    return Array.from(eventTypes).sort();
  };

  if (loading) {
    return (
      <div className="payment-logs">
        <div className="loading">Loading payment logs...</div>
      </div>
    );
  }

  return (
    <div className="payment-logs">
      <h1>Payment Logs</h1>

      <div className="filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)} className="form-control">
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Event Type:</label>
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className="form-control">
            <option value="all">All Events</option>
            {getEventTypes().map((eventType) => (
              <option key={eventType} value={eventType}>
                {eventType}
              </option>
            ))}
          </select>
        </div>

        <button onClick={loadLogs} className="btn btn-primary">
          Refresh
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="logs-table">
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Event</th>
              <th>Status</th>
              <th>Registration ID</th>
              <th>Amount</th>
              <th>Convenience Fee</th>
              <th>Stripe Payment ID</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                  No logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className={log.success ? 'success' : 'failed'}>
                  <td>{formatDate(log.timestamp)}</td>
                  <td>{log.event}</td>
                  <td>
                    <span className={`status-badge ${log.success ? 'success' : 'failed'}`}>
                      {log.success ? '✓' : '✗'}
                    </span>
                  </td>
                  <td>
                    {log.registrationId ? (
                      <a href={`/admin/registration-details/${log.registrationId}`} target="_blank" rel="noopener noreferrer">
                        {log.registrationId.substring(0, 8)}...
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{formatCurrency(log.amount)}</td>
                  <td>{formatCurrency(log.convenienceFee)}</td>
                  <td>
                    {log.stripePaymentId ? (
                      <small style={{ fontFamily: 'monospace' }}>{log.stripePaymentId.substring(0, 20)}...</small>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{log.error || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentLogs;

